import React, { Suspense, lazy } from "react";
import { useTheme, Box } from "@mui/material";
import { Navigate, Routes, Route, useLocation } from "react-router-dom";
import { Loading } from "../../loading/loading.component";
import MessageBox from "../../messageBox/messageBoxContainer";
import { useImpersonationStore } from "../../../stores/useImpersonationStore";

const DashboardContainer = lazy(() => import("../../../screens/dashboard/dashboardContainer"));
const DataholderContainer = lazy(() => import("../../../screens/dataholder/dataholderContainer"));
const InvoicesContainer = lazy(() => import("../../../screens/invoices/invoicesContainer"));
const ConsumptionsContainer = lazy(() => import("../../../screens/consumptions/consumptionsContainer"));
const ContractsContainer = lazy(() => import("../../../screens/contracts/contractsContainer"));
const ContractDetailContainer = lazy(() => import("../../../screens/contracts/contractDetail/ContractDetailContainer"));
const ProfileContainer = lazy(() => import("../../../screens/profile/profileContainer"));
const ChangesContainer = lazy(() => import("../../../screens/changes/changesContainer"));
const AlertasContainer = lazy(() => import("../../../screens/alertas/alertasContainer"));
const InformesContainer = lazy(() => import("../../../screens/informes/informesContainer"));
const AyudaContainer = lazy(() => import("../../../screens/ayuda/ayudaContainer"));
const GestionesContainer = lazy(() => import("../../../screens/gestiones/gestionesContainer"));
const GestionDetailContainer = lazy(() => import("../../../screens/gestiones/gestionDetailContainer"));
const GestionCreateContainer = lazy(() => import("../../../screens/gestiones/gestionCreateContainer"));
const NewsContainer = lazy(() => import("../../../screens/news/newsContainer"));
const NewsDetailContainer = lazy(() => import("../../../screens/news/newsDetailContainer"));

const GestionCreateRoute = () => {
  const isSuperuser = useImpersonationStore(state => state.isSuperuser);

  return isSuperuser
    ? <Navigate to="/tasks" replace />
    : <GestionCreateContainer />;
};

// Keep bookmarked Spanish URLs working while making English URLs canonical.
const LegacyRouteRedirect = ({ from, to }) => {
  const location = useLocation();
  const pathname = to + location.pathname.slice(from.length);

  return (
    <Navigate
      to={{ pathname, search: location.search, hash: location.hash }}
      state={location.state}
      replace
    />
  );
};

// Desktop navigation: branding (58px) + links (44px).
const NAV_HEIGHT = 102;

const Content = () => {
  const theme = useTheme();
  const location = useLocation();

  const styles = {
    content: {
      flexGrow: 1,
      marginTop: `${NAV_HEIGHT}px`, // Space for fixed top navigation
      paddingTop: location.pathname === "/" ? 0 : theme.spacing(2),
      paddingBottom: location.pathname === "/" ? 0 : theme.spacing(4),
      [theme.breakpoints.down('sm')]: {
        marginTop: '64px', // Only primary bar on mobile
      }
    }
  };

  return (
    <Box component="main" sx={styles.content}>
      <MessageBox />
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<DashboardContainer />} />
          <Route path="/dataholder" element={<DataholderContainer />} />
          <Route path="/invoices" element={<InvoicesContainer />} />
          <Route path="/consumptions" element={<ConsumptionsContainer />} />
          <Route path="/consumptions/:contract/:cups/:initDate/:endDate" element={<ConsumptionsContainer />} />
          <Route path="/contracts" element={<ContractsContainer />} />
          <Route path="/contracts/:id" element={<ContractDetailContainer />} />
          <Route path="/profile" element={<ProfileContainer />} />
          <Route path="/changes" element={<ChangesContainer />} />
          <Route path="/alerts" element={<AlertasContainer />} />
          <Route path="/reports" element={<InformesContainer />} />
          <Route path="/help" element={<AyudaContainer />} />
          <Route path="/tasks" element={<GestionesContainer />} />
          <Route path="/tasks/new" element={<GestionCreateRoute />} />
          <Route path="/tasks/:id" element={<GestionDetailContainer />} />
          <Route path="/alertas" element={<LegacyRouteRedirect from="/alertas" to="/alerts" />} />
          <Route path="/informes" element={<LegacyRouteRedirect from="/informes" to="/reports" />} />
          <Route path="/ayuda" element={<LegacyRouteRedirect from="/ayuda" to="/help" />} />
          <Route path="/gestiones" element={<LegacyRouteRedirect from="/gestiones" to="/tasks" />} />
          <Route path="/gestiones/nueva" element={<LegacyRouteRedirect from="/gestiones/nueva" to="/tasks/new" />} />
          <Route path="/gestiones/:id" element={<LegacyRouteRedirect from="/gestiones" to="/tasks" />} />
          <Route path="/news" element={<NewsContainer />} />
          <Route path="/news/:id" element={<NewsDetailContainer />} />
          <Route path="*" element={<DashboardContainer />} />
        </Routes>
      </Suspense>
    </Box>
  );
};

export default Content;
